import type { ReactNode } from "react";
import { legalAnchor, legalBlocks, type DocBlock } from "@alm/domain/legal";
import { LegalQuote } from "@/components/marketing/site/legal/legal-quote";
import {
  LegalSpans,
  plainLink,
  type LegalLink,
} from "@/components/marketing/site/legal/legal-spans";
import { LegalTable } from "@/components/marketing/site/legal/legal-table";

const META = /^(Last updated|Version) /;

const NO_NOTES: Record<string, ReactNode> = {};

const textOf = (block: DocBlock) =>
  "spans" in block ? block.spans.map((span) => span.text).join("") : "";

/** The published contract, from the Markdown the order records name by version. Every section and
 * clause carries the id a reader deep-links to, and `notes` adds a line of the page's own under a
 * heading, outside the text. */
export function LegalDocument({
  markdown,
  link = plainLink,
  linkBaa = false,
  notes = NO_NOTES,
}: {
  markdown: string;
  link?: LegalLink;
  /** Link mentions of the BAA to /baa, up to the BAA's own text. */
  linkBaa?: boolean;
  notes?: Record<string, ReactNode>;
}) {
  const blocks = legalBlocks(markdown);
  const baaAt = blocks.findIndex(
    (block) => block.k === "heading" && legalAnchor(textOf(block)) === "appendix-b",
  );
  return (
    <div className="flex flex-col gap-4 text-[15px] leading-relaxed text-foreground/90">
      {blocks.map((block, index) => {
        const key = `block-${index}`;
        const anchor = legalAnchor(textOf(block));
        const linking = linkBaa && (baaAt === -1 || index < baaAt);
        if (block.k === "heading") {
          const spans = <LegalSpans spans={block.spans} link={link} linkBaa={false} />;
          if (block.level === 1) {
            return (
              <h1
                key={key}
                className="font-display text-3xl font-semibold tracking-tight sm:text-4xl"
              >
                {spans}
              </h1>
            );
          }
          const Tag = block.level === 2 ? "h2" : "h3";
          return (
            <div key={key} className="flex flex-col gap-2">
              <Tag
                id={anchor ?? undefined}
                className={
                  block.level === 2
                    ? "mt-8 scroll-mt-24 text-xl font-semibold tracking-tight"
                    : "mt-4 scroll-mt-24 text-base font-semibold"
                }
              >
                {spans}
              </Tag>
              {anchor && notes[anchor]}
            </div>
          );
        }
        if (block.k === "p") {
          const meta = META.test(textOf(block));
          return (
            <p
              key={key}
              id={anchor ?? undefined}
              className={meta ? "text-sm text-muted-foreground" : "scroll-mt-24"}
            >
              <LegalSpans spans={block.spans} link={link} linkBaa={linking} />
            </p>
          );
        }
        if (block.k === "quote") {
          return (
            <LegalQuote key={key} paragraphs={block.paragraphs} link={link} linkBaa={linking} />
          );
        }
        if (block.k === "table") return <LegalTable key={key} table={block} link={link} />;
        return null;
      })}
    </div>
  );
}
